import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  LinearProgress
} from '@mui/material';

/**
 * Import NIST Assessment Dialog
 * 
 * Allows importing a NIST CSF assessment from a Word document (.docx)
 * with completion date specification.
 */
const ImportNistDialog = ({ 
  open, 
  onClose, 
  clientId,
  clientName,
  onImport 
}) => {
  const [file, setFile] = useState(null);
  const [completionDate, setCompletionDate] = useState('');
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.docx')) {
        setError('Only .docx files are supported');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  };

  const handleImport = async () => {
    // Validation
    if (!file) {
      setError('Please select a file');
      return;
    }
    
    if (!completionDate) {
      setError('Please select completion date');
      return;
    }
    
    setImporting(true);
    setError('');
    
    try {
      await onImport(file, completionDate);
      
      // Reset form
      setFile(null);
      setCompletionDate('');
      onClose();
      
    } catch (error) {
      console.error('Error importing assessment:', error);
      setError(error.response?.data?.error || error.message || 'Failed to import assessment');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    if (!importing) {
      setFile(null);
      setCompletionDate('');
      setError('');
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Import NIST Assessment
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Import for: <strong>{clientName}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upload a NIST Cybersecurity Framework assessment Word document (.docx) from another system.
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Required columns in Word document:</strong>
          </Typography>
          <Typography variant="body2" component="div">
            • <strong>Item</strong>: Contains subcategory codes (e.g., "ID.AM-1")<br/>
            • <strong>Response</strong>: Met, Not Met, Partially Met, or N/A<br/>
            • <strong>Comments</strong>: Regular notes (optional)<br/>
            • <strong>Private Comments</strong>: Internal notes (optional)
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Assessment Completion Date *
          </Typography>
          <TextField
            type="date"
            fullWidth
            value={completionDate}
            onChange={(e) => setCompletionDate(e.target.value)}
            disabled={importing}
            InputLabelProps={{
              shrink: true,
            }}
            helperText="When was the original assessment completed?"
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Word Document (.docx) *
          </Typography>
          <input
            type="file"
            accept=".docx"
            onChange={handleFileChange}
            disabled={importing}
            style={{
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              width: '100%',
              cursor: importing ? 'not-allowed' : 'pointer'
            }}
          />
          {file && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
            </Typography>
          )}
        </Box>

        {importing && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
              Importing assessment... This may take a moment.
            </Typography>
          </Box>
        )}

        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> The imported assessment will be automatically scored and marked as completed.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleClose}
          disabled={importing}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleImport}
          variant="contained"
          disabled={importing || !file || !completionDate}
          sx={{ 
            backgroundColor: '#27ae60',
            '&:hover': { backgroundColor: '#229954' }
          }}
        >
          {importing ? 'Importing...' : 'Import Assessment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportNistDialog;
